import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Search,
  TrendingUp,
  AlertTriangle,
  BarChart3,
  FileText,
  Lightbulb,
  FolderOpen,
  Link,
  Download,
} from "lucide-react";
import ScoreGaugePanel from "@/components/ruby-ai-v2/dashboard/ScoreGaugePanel";
import type { SubNavItem } from "@/components/ruby-ai-v2/dashboard/ScoreGaugePanel";
import AskRubyWidget from "@/components/ruby-ai-v2/dashboard/AskRubyWidget";
import type { AuditResult } from "@/components/ruby-ai-v2/services/auditService";
import { loadLatestAudits, loadLatestAuditByShortcut } from "@/components/ruby-ai-v2/services/auditStorageService";
import { useSiteContext } from "@/components/ruby-ai-v2/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import RubyExportDialog from "@/components/ruby-ai-v2/reports/RubyExportDialog";
import { buildFullExportData } from "@/components/ruby-ai-v2/reports/exportDataUtils";
import SiteSwitcher from "@/components/ruby-ai-v2/dashboard/SiteSwitcher";
import ReportSwitcher from "@/components/ruby-ai-v2/dashboard/ReportSwitcher";
import OverviewTabContent from "@/components/ruby-ai-v2/dashboard/tabs/OverviewTabContent";
import KeywordsTabContent from "@/components/ruby-ai-v2/dashboard/tabs/KeywordsTabContent";
import CompetitorsTabContent from "@/components/ruby-ai-v2/dashboard/tabs/CompetitorsTabContent";
import TechnicalTabContent from "@/components/ruby-ai-v2/dashboard/tabs/TechnicalTabContent";
import ContentTabContent from "@/components/ruby-ai-v2/dashboard/tabs/ContentTabContent";
import DiscoveryTabContent from "@/components/ruby-ai-v2/dashboard/tabs/DiscoveryTabContent";
import BacklinksTabContent from "@/components/ruby-ai-v2/dashboard/tabs/BacklinksTabContent";

const tabs = [
  { id: "overview", label: "Overview", icon: Search },
  { id: "keywords", label: "Keywords", icon: BarChart3 },
  { id: "competitors", label: "Competitors", icon: TrendingUp },
  { id: "technical", label: "Technical", icon: AlertTriangle },
  { id: "content", label: "Content", icon: FileText },
  { id: "discovery", label: "Discovery", icon: Lightbulb },
  { id: "backlinks", label: "Backlinks", icon: Link },
] as const;

type TabId = (typeof tabs)[number]["id"];

// ── Hero data defaults (zeros until audit populates them) ──

const heroDefaults: Record<TabId, {
  badge: string;
  title: string;
  centerLabel: string;
  subtitle: string;
  metricLabels: string[];
}> = {
  overview: { badge: "Search Intelligence", title: "Search Intelligence Score", centerLabel: "OVERALL", subtitle: "Run a Full SEO Audit to see your overall score.", metricLabels: ["Authority", "Traffic", "Keywords", "Technical"] },
  keywords: { badge: "Keyword Rankings", title: "Keyword Performance Score", centerLabel: "KEYWORDS", subtitle: "Run a Keyword Rankings audit to see your keyword data.", metricLabels: ["Total Keywords", "Top 10", "Avg. Position", "Traffic Potential"] },
  competitors: { badge: "Competitor Analysis", title: "Competitor Gap Score", centerLabel: "COMPETITORS", subtitle: "Run a Competitor Analysis to see your competitive position.", metricLabels: ["Competitors", "Your Authority", "Your Traffic", "Your Keywords"] },
  technical: { badge: "Technical SEO", title: "Technical Health Score", centerLabel: "HEALTH", subtitle: "Run a Technical SEO audit to check your site health.", metricLabels: ["Score", "Critical Issues", "Warnings", "Content Length"] },
  content: { badge: "Content Analysis", title: "Content Quality Score", centerLabel: "QUALITY", subtitle: "Run a Content Gaps aud it to assess content quality.", metricLabels: ["Score", "Content Gaps", "Avg. Overlap %", "Competitor Traffic"] },
  discovery: { badge: "Discovery", title: "Opportunity Score", centerLabel: "POTENTIAL", subtitle: "Run a Keyword Discovery audit to find opportunities.", metricLabels: ["Keywords Found", "Avg. Difficulty", "Avg. cpc", "Total Volume"] },
  backlinks: { badge: "Backlinks", title: "Link Authority Score", centerLabel: "LINKS", subtitle: "Run a Backlinks audit to analyse your link profile.", metricLabels: ["Authority", "Total Backlinks", "Referring Domains", "Anchor Diversity"] },
};

function formatTimeAgo(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(isoDate).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

// Sub-nav items per tab — each maps to a shortcutId used by the audit service
const TAB_SUBNAV: Record<TabId, Omit<SubNavItem, "hasData">[]> = {
  overview: [
    { id: "full-audit", label: "Full Audit", shortcutId: "full-seo-audit" },
    { id: "traffic", label: "Traffic & Trends", shortcutId: "traffic-trends" },
    { id: "authority", label: "Authority", shortcutId: "authority-trust" },
  ],
  keywords: [
    { id: "rankings", label: "Rankings", shortcutId: "keyword-rankings" },
    { id: "discovery", label: "Discovery", shortcutId: "keyword-discovery" },
    { id: "serp", label: "SERP", shortcutId: "serp-analysis" },
  ],
  competitors: [
    { id: "competitor", label: "Competitor Analysis", shortcutId: "competitor-analysis" },
  ],
  technical: [
    { id: "tech-audit", label: "Technical Audit", shortcutId: "technical-seo" },
    { id: "page-audit", label: "Page Audit", shortcutId: "page-level-audit" },
  ],
  content: [
    { id: "content-gaps", label: "Content Gaps", shortcutId: "content-gaps" },
    { id: "domain-pages", label: "Domain Pages", shortcutId: "domain-pages" },
  ],
  discovery: [
    { id: "kw-discovery", label: "Keyword Discovery", shortcutId: "keyword-discovery" },
    { id: "serp-disc", label: "SERP", shortcutId: "serp-analysis" },
  ],
  backlinks: [
    { id: "backlink-analysis", label: "Backlink Analysis", shortcutId: "backlinks" },
    { id: "auth-trust", label: "Authority", shortcutId: "authority-trust" },
  ],
};

const SearchIntelligenceDashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const shortcutId = location.state?.shortcutId;
  const { activeSite, selectedReportId } = useSiteContext();
  const { user } = useAuth();
  const userId = user?.id;
  const auditContextKey = JSON.stringify([userId, activeSite?.domain, selectedReportId]);
  const auditContextRef = useRef(auditContextKey);
  auditContextRef.current = auditContextKey;

  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [liveAuditData, setLiveAuditData] = useState<AuditResult | null>(null);
  const [activeSubNav, setActiveSubNav] = useState<string>(TAB_SUBNAV.overview[0].id);
  const [availableAudits, setAvailableAudits] = useState<Set<string>>(new Set());
  const [lastAuditMeta, setLastAuditMeta] = useState<{ region: string; domain: string; createdAt: string } | null>(null);
  const [exportDialog, setExportDialog] = useState<{ open: boolean; title: string; tier: "widget" | "chat" | "full"; data: any[]; auditResult?: any }>({ open: false, title: "", tier: "widget", data: [] });

  const openFullReportExport = async () => {
    if (!liveAuditData) return;
    const exportRows = buildFullExportData(liveAuditData, liveAuditData.requestedAuditType || "full-seo-audit");
    setExportDialog({ open: true, title: "Search Intelligence Full Report", tier: "full", data: exportRows, auditResult: liveAuditData });
  };

  // // Resource vault (shortcutId exists)
  // useEffect(() => {
  //   if (!shortcutId) return;

  //   for (const [tabKey, items] of Object.entries(TAB_SUBNAV)) {
  //     const match = items.find((item) => item.shortcutId === shortcutId);

  //     if (match) {
  //       setActiveTab(tabKey as TabId);
  //       setActiveSubNav(match.id);

  //       loadLatestAuditByShortcut({
  //         userId,
  //         cardId: "search-intelligence",
  //         shortcutId: match.shortcutId,
  //       }).then((saved) => {
  //         if (saved) {
  //           setLiveAuditData(saved.structured_data as any);
  //           setLastAuditMeta({
  //             region: saved.region,
  //             domain: saved.domain,
  //             createdAt: saved.created_at,
  //           });
  //         }
  //       });

  //       break;
  //     }
  //   }
  // }, [shortcutId, userId]);

  // When tab changes, reset sub-nav to the first item for that tab and fetch audit data using shortcut_id 
  // useEffect(() => {
  //   if (shortcutId) return; // prevent override
  //   const firstItem = TAB_SUBNAV[activeTab]?.[0];

  //   if (firstItem) {
  //     setActiveSubNav(firstItem.id);

  //     // 🔥 Fetch data for default sub-nav of this tab
  //     loadLatestAuditByShortcut({
  //       userId,
  //       cardId: "search-intelligence",
  //       shortcutId: firstItem.shortcutId,
  //     }).then((saved) => {
  //       if (saved) {
  //         console.log(`📂 [Auto Load] ${firstItem.shortcutId}`, saved.domain);
  //         setLiveAuditData(saved.structured_data as any);
  //         setLastAuditMeta({
  //           region: saved.region,
  //           domain: saved.domain,    
  //           createdAt: saved.created_at,
  //         });
  //       } else {
  //         console.log(`📭 [Auto Load] No data for ${firstItem.shortcutId}`);
  //         setLiveAuditData(null);
  //       }
  //     });
  //   }
  // }, [activeTab, userId]);

  useEffect(() => {
    if (!userId) return;
    if (activeSite?.domain) {
      loadDashboardAudit(activeSite.domain);
    } else {
      setLiveAuditData(null);
      setLastAuditMeta(null);
    }
  }, [activeSite?.domain, userId, selectedReportId]);

  // On mount, check which audit types have saved data (for green dot indicators)
  useEffect(() => {
    if (!userId) return;
    loadLatestAudits({ userId, cardId: "search-intelligence", limit: 50 }).then((audits) => {
      const found = new Set<string>();
      for (const a of audits) {
        found.add(a.shortcut_id);
      }
      setAvailableAudits(found);
      console.log("📂 [Dashboard] Available audit types:", [...found]);

    }).catch((err) => console.warn("[Dashboard] Failed to load saved audit:", err));
  }, [userId]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const mainEl = document.querySelector("main");
    if (mainEl) mainEl.scrollTop = 0;
  }, []);

  const loadDashboardAudit = async (domain?: string) => {
    if (!userId || !domain) return;
    setLiveAuditData(null);
    setLastAuditMeta(null);
    if (!domain) {
      setLiveAuditData(null);
      setLastAuditMeta(null);
      return;
    }
    const audit = await loadLatestAuditByShortcut({ userId, cardId: "search-intelligence", shortcutId: "authority44-import", domain, reportId: selectedReportId });
    if (auditContextRef.current !== auditContextKey) return;
    if (audit) {
      setLiveAuditData(audit.structured_data as AuditResult);
      setLastAuditMeta({ region: audit.region, domain: audit.domain, createdAt: audit.created_at });
      return;
    } else { 
      setLiveAuditData(null);
      setLastAuditMeta(null);
    }
  };

  const handleSubNavClick = async (item: SubNavItem) => {
    if (!userId) return;
    setActiveSubNav(item.id);
    const domain = activeSite?.domain;
    const audit = await loadLatestAuditByShortcut({ userId, cardId: "search-intelligence", shortcutId: item.shortcutId, domain });
    if (auditContextRef.current !== auditContextKey) return;
    if (audit) {
      setLiveAuditData(audit.structured_data as AuditResult);
      setLastAuditMeta({ region: audit.region, domain: audit.domain, createdAt: audit.created_at });
      return;
    } else {
      setLiveAuditData(null);
      setLastAuditMeta(null);
    }
  };

  const currentSubNavItem = TAB_SUBNAV[activeTab].find((item) => item.id === activeSubNav);

  const isExportEnabled = liveAuditData && currentSubNavItem?.shortcutId === liveAuditData.requestedAuditType;


  return (
    <div
      className="relative min-h-screen w-full overflow-x-hidden"
      style={{ background: "#08080E" }}
    >
      {/* Ambient corner glows */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(620px 420px at 88% -6%, rgba(139,92,246,.15), transparent 70%), radial-gradient(560px 460px at 102% 108%, rgba(255,122,24,.10), transparent 70%)",
          zIndex: 0,
        }}
      />
      {/* Faint grid */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          opacity: 0.4,
          zIndex: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.025) 1px, transparent 1px)",
          backgroundSize: "46px 46px",
          WebkitMaskImage: "radial-gradient(circle at 50% 20%, #000 30%, transparent 80%)",
          maskImage: "radial-gradient(circle at 50% 20%, #000 30%, transparent 80%)",
        }}
      />

      {/* Content (accent scope — swap --accent per card) */}
      <div
        className="relative z-[1] px-6 md:px-10 lg:px-14 py-6 mx-auto"
        style={{ ["--accent" as string]: "#8B5CF6", maxWidth: "1480px" } as React.CSSProperties}
      >
        {/* ── Header ── */}
        <div className="flex items-center gap-3.5 mb-1.5">
          <button
            onClick={() => navigate("/dashboard")}
            aria-label="Back to dashboard"
            className="grid place-items-center w-[38px] h-[38px] flex-none rounded-[10px] border border-white/[0.07] bg-og01-card text-og01-text-secondary transition-colors hover:border-white/[0.12] hover:text-og01-text"
          >
            <ArrowLeft className="w-[18px] h-[18px]" />
          </button>
          <SiteSwitcher onSiteChange={(domain) => loadDashboardAudit(domain)} />
          <ReportSwitcher />
          <h1 className="flex items-center gap-2.5 font-heading text-[30px] leading-none text-og01-text">
            <Search className="w-[26px] h-[26px]" style={{ color: "var(--accent)" }} />
            Search Intelligence
          </h1>

          <div className="flex-1" />

          <button
            onClick={openFullReportExport}
            disabled={!isExportEnabled}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-[10px] border border-white/[0.07] bg-og01-card text-[13.5px] font-semibold text-og01-text transition-colors hover:border-white/[0.12] disabled:opacity-45 disabled:cursor-not-allowed"
          >
            <Download className="w-[15px] h-[15px]" />Export Full Report
          </button>
          <button
            onClick={() => navigate("/resource-vault?card=search-intelligence")}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-[10px] border-none text-[13.5px] font-semibold text-white transition-all hover:brightness-110"
            style={{ background: "linear-gradient(98deg,#8B5CF6 0%,#B16CEA 46%,#FF7A18 100%)", boxShadow: "0 8px 26px -12px rgba(255,122,24,.6)" }}
          >
            <FolderOpen className="w-[15px] h-[15px]" />Saved Reports
          </button>
        </div>

        {/* Metadata row — populated from last audit */}
        {lastAuditMeta && (
          <div className="flex items-center gap-5 mb-4 pl-[52px] text-[12.5px] text-og01-text-muted">
            {[
              lastAuditMeta.region ? <>Market: <b className="font-medium text-og01-text-secondary">{lastAuditMeta.region.toUpperCase()}</b></> : null,
              lastAuditMeta.domain ? <>Domain: <b className="font-medium text-og01-text-secondary">{lastAuditMeta.domain}</b></> : null,
              lastAuditMeta.createdAt ? <>Last Run: <b className="font-medium text-og01-text-secondary">{formatTimeAgo(lastAuditMeta.createdAt)}</b></> : null,
            ].filter(Boolean).map((item, i) => (
              <span key={i}>{item}</span>
            ))}
          </div>
        )}

        {/* ── Tab Navigation ── */}
        <div className="flex items-center gap-1 mb-5 p-1.5 rounded-[14px] overflow-x-auto border border-white/[0.07] bg-og01-card">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-2 px-[15px] py-2.5 rounded-[9px] text-[13.5px] font-medium whitespace-nowrap transition-all hover:text-og01-text"
                style={
                  isActive
                    ? {
                        color: "var(--accent)",
                        background: "color-mix(in srgb, var(--accent) 12%, transparent)",
                        border: "1px solid color-mix(in srgb, var(--accent) 28%, transparent)",
                      }
                    : { color: "#9AA0B3", border: "1px solid transparent" }
                }
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ── Score Gauge Panel (full-width hero — live data or zeros) ── */}
        <div className="mb-6">
          {(() => {
            const defaults = heroDefaults[activeTab];
            const dm = liveAuditData?.domainMetrics;
            const cm = liveAuditData?.currentMetrics;
            const ts = liveAuditData?.technicalSEO;
            const bk = liveAuditData?.backlinks
            const hasLive = !!liveAuditData;

            let score = 0;
            let subtitle = defaults.subtitle;
            let metrics: { label: string; value: string; changeDirection?: "up" | "down" | "neutral" }[] =
              defaults.metricLabels.map((l) => ({ label: l, value: "0" }));
            if (hasLive) {
              subtitle = `Live audit for ${lastAuditMeta.domain}`;
              switch (activeSubNav) {
                case "full-audit":
                  score = liveAuditData.cardScore || 0;
                  metrics = [
                    { label: "Authority", value: String(dm?.authorityScore ?? 0) },
                    { label: "Traffic", value: (dm?.organicTraffic ?? 0).toLocaleString() },
                    { label: "Keywords", value: (dm?.organicKeywords ?? 0).toLocaleString() },
                    { label: "Technical", value: ts ? `${ts.score}%` : "—" },
                  ];
                  break;
                case "traffic":
                  score = cm?.authorityScore || 0;
                  metrics = [
                    { label: "Authority", value: String(cm?.authorityScore ?? 0) },
                    { label: "Traffic", value: (cm?.organicTraffic ?? 0).toLocaleString() },
                    { label: "Keywords", value: (cm?.organicKeywords ?? 0).toLocaleString() },
                    { label: "Cost", value: `$${(cm?.organicCost ?? 0).toLocaleString()}` },
                  ];
                  break;  
                case "authority":
                  score = bk?.domainAuthority?.domainAuthority || 0;
                  metrics = [
                    { label: "Domain Authority", value: String(bk?.domainAuthority?.domainAuthority ?? 0) },
                    { label: "Page Authority", value: (bk?.domainAuthority?.pageAuthority ?? 0).toLocaleString() },
                    { label: "Backlinks", value: (bk?.total ?? 0).toLocaleString() },
                    { label: "Referring Domains", value: (bk?.referringDomains ?? 0).toLocaleString() },
                  ];
                  break;    
                case "rankings":
                  score = Math.min(100, liveAuditData.keywords?.length || 0);
                  metrics = [
                    { label: "Total Keywords", value: String(liveAuditData.keywords?.length ?? 0) },
                    { label: "Top 10", value: String((liveAuditData.keywords || []).filter((k: any) => k.position <= 10).length) },
                    { label: "Avg. Position", value: liveAuditData.keywords?.length ? (liveAuditData.keywords.reduce((sum: number, k: any) => sum + k.position, 0) / liveAuditData.keywords.length).toFixed(1) : "0" },
                    { label: "Traffic Potential", value: (liveAuditData.keywords || []).reduce((sum: number, k: any) => sum + (k.trafficPotential || 0), 0).toLocaleString() },
                  ];
                  break;
                case "discovery":
                  score = Math.min(100, liveAuditData.keywords?.length || 0);
                  metrics = [
                    { label: "Total Keywords", value: String(liveAuditData.keywords?.length ?? 0) },
                    { label: "High Volume", value: String((liveAuditData.keywords || []).filter((k: any) => k.volume > 100000).length) },
                    { label: "Avg. Difficulty", value: liveAuditData.keywords?.length ? (liveAuditData.keywords.reduce((sum: number, k: any) => sum + k.difficulty, 0) / liveAuditData.keywords.length).toFixed(1) : "0" },
                    { label: "Total Volume", value: (liveAuditData.keywords || []).reduce((sum: number, k: any) => sum + (k.volume || 0), 0).toLocaleString() },
                  ];
                  break;
                case "serp":
                case "serp-disc":  
                  score = Math.min(100, liveAuditData.serpFeatures?.length || 0);
                  metrics = [
                    { label: "Total Keywords", value: String(liveAuditData?.serpFeatures?.length ?? 0) },
                    { label: "Traffic Potential", value: (liveAuditData?.serpFeatures || []).reduce((sum: number, k: any) => sum + (k.traffic || 0), 0).toLocaleString() },
                    { label: "Avg. Position", value: liveAuditData?.serpFeatures?.length ? (liveAuditData?.serpFeatures.reduce((sum: number, k: any) => sum + k.position, 0) / liveAuditData?.serpFeatures.length).toFixed(1) : "0" },
                    { label: "Total Volume", value: (liveAuditData?.serpFeatures || []).reduce((sum: number, k: any) => sum + (k.volume || 0), 0).toLocaleString() },
                  ];
                  break;
                case "competitor":
                  score = liveAuditData.competitors?.length ? 50 : 0;
                  metrics = [
                    { label: "Competitors", value: String(liveAuditData.competitors?.length ?? 0) },
                    { label: "Your Authority", value: String(dm?.authorityScore ?? 0) },
                    { label: "Your Traffic", value: (dm?.organicTraffic ?? 0).toLocaleString() },
                    { label: "Your Keywords", value: (dm?.organicKeywords ?? 0).toLocaleString() },
                  ];
                  break;  
                case "tech-audit":
                case "page-audit":
                  score = ts?.score || 0;
                  metrics = [
                    { label: "Score", value: ts ? `${ts.score}/100` : "0" },
                    { label: "Critical Issues", value: String(ts?.issues?.filter((i: any) => i.severity === "critical").length ?? 0) },
                    { label: "Warnings", value: String(ts?.issues?.filter((i: any) => i.severity === "warning").length ?? 0) },
                    { label: "Content Length", value: ts ? `${ts.contentLength} chars` : "0" },
                  ];
                  break;
                case "content-gaps":
                  score = ts?.score || 0
                  metrics = [
                    { label: "Score", value: ts ? `${ts?.score}/100` : "0" },
                    { label: "Content Gaps", value: String(liveAuditData?.competitors?.flatMap((c: any) => c.keywordGaps || []).length || "0") },
                    { label: "Avg. Overlap %", value: liveAuditData?.competitors?.length ? (
                        liveAuditData?.competitors.reduce((sum: number, c: any) => sum + (c.overlap || 0), 0) / liveAuditData?.competitors.length ).toFixed(1) + "%": "0%" },
                    { label: "Competitor Traffic", value: (liveAuditData?.competitors || []).reduce((sum: number, c: any) => sum + (c.traffic || 0), 0).toLocaleString() },
                  ];
                  break;
                case "domain-pages":
                  score = ts?.score || 0;
                  metrics = [
                    { label: "Score", value: ts ? `${ts?.score}/100` : "0" },
                    { label: "Total Pages", value: String(liveAuditData?.topPages?.length ?? 0) },
                    { label: "Thin Pages", value: String(liveAuditData?.topPages?.filter((p: any) => (p.keywordsCount || 0) < 5 || (p.trafficSum || 0) < 100 ).length || '0') },
                    { label: "Refresh Needed", value: String(liveAuditData?.topPages?.filter((p: any) => (p.keywordsCount || 0) > 0 && (p.trafficSum || 0) < 50  ).length || '0')},
                  ];
                  break;  
                  case "kw-discovery":
                    score = liveAuditData.keywords?.length ? Math.min(100, liveAuditData.keywords.length * 2) : 0;
                    metrics = [
                      { label: "Keywords Found", value: String(liveAuditData?.keywords?.length ?? 0) },
                      { label: "Avg. Difficulty", value: liveAuditData?.keywords?.length ? (liveAuditData?.keywords.reduce((s: number, k: any) => s + (k.difficulty || 0), 0) / liveAuditData.keywords.length).toFixed(0) : "0" },
                      { label: "Avg. cpc", value: (liveAuditData?.keywords || []).reduce((s: number, k: any) => s + (k.cpc || 0), 0).toLocaleString() },
                      { label: "Total Volume", value: (liveAuditData?.keywords || []).reduce((sum: number, k: any) => sum + (k.volume || 0), 0).toLocaleString() },
                    ];
                    break;
                case "backlink-analysis":
                case "auth-trust":
                  score = bk?.domainAuthority?.domainAuthority || 0;
                  metrics = [
                    { label: "Authority", value: bk?.domainAuthority ? `${bk?.domainAuthority?.domainAuthority }/100` : "0" },
                    { label: "Total Backlinks", value: (bk?.total ?? 0).toLocaleString() },
                    { label: "Referring Domains", value: (bk?.referringDomains ?? 0).toLocaleString() },
                    { label: "Anchor Diversity", value: (bk?.anchorDiversity ?? 0).toLocaleString() }
                  ];
                  break;
              }
            }

            return (
              <ScoreGaugePanel
                score={score}
                scoreChange={0}
                period="Last 30 Days"
                subtitle={subtitle}
                metrics={metrics}
                badge={defaults.badge}
                title={defaults.title}
                centerLabel={defaults.centerLabel}
                subNavItems={TAB_SUBNAV[activeTab].map((item) => ({
                  ...item,
                  hasData: availableAudits.has(item.shortcutId),
                }))}
                activeSubNav={activeSubNav}
                onSubNavClick={handleSubNavClick}
              />
            );
          })()}
        </div>

        {/* ── Two-column layout: Data panels + Ask Ruby ── */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Left column — tab-switched data panels (3/5 width) */}
          <div className="lg:col-span-3 space-y-6" style={{ minHeight: "calc(100vh - 200px)" }}>
            {activeTab === "overview" && <OverviewTabContent liveData={liveAuditData} activeSubNav={activeSubNav} />}
            {activeTab === "keywords" && <KeywordsTabContent liveData={liveAuditData} activeSubNav={activeSubNav} />}
            {activeTab === "competitors" && <CompetitorsTabContent liveData={liveAuditData} />}
            {activeTab === "technical" && <TechnicalTabContent liveData={liveAuditData} activeSubNav={activeSubNav} />}
            {activeTab === "content" && <ContentTabContent liveData={liveAuditData} activeSubNav={activeSubNav} />}
            {activeTab === "discovery" && <DiscoveryTabContent liveData={liveAuditData} activeSubNav={activeSubNav} />}
            {activeTab === "backlinks" && <BacklinksTabContent liveData={liveAuditData} activeSubNav={activeSubNav} />}
          </div>

          {/* Right column — Ask Ruby Chat (2/5 width, stretches full height) */}
          <div className="lg:col-span-2 flex flex-col">
            <div className="sticky top-6 flex-1" style={{ height: "calc(100vh - 48px)" }}>
              <AskRubyWidget activeTab={activeTab} onIssueFixed={() => loadDashboardAudit(activeSite?.domain)} />
            </div>
          </div>
        </div>
      </div>

      <RubyExportDialog
        open={exportDialog.open}
        onClose={() => setExportDialog((p) => ({ ...p, open: false }))}
        title={exportDialog.title}
        tier={exportDialog.tier}
        data={exportDialog.data}
        fileName={`OG01_Search_Intelligence_${lastAuditMeta?.domain || "report"}`}
        auditResult={exportDialog.auditResult}
        domain={lastAuditMeta?.domain}
      />
    </div>
  );
};

export default SearchIntelligenceDashboard;

